import React, { useState } from 'react';
import './AdminPayoutStatusModal.css';

export type AdminPayoutStatusSource = 'poolpay' | 'viyona';

interface AdminPayoutStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  statusData: any;
  loading?: boolean;
}

const poolPayTxnColor = (transactionStatus: string | undefined): string => {
  const u = (transactionStatus || '').trim().toLowerCase();
  if (u === 'completed') return '#10B981';
  if (u === 'failed') return '#EF4444';
  if (u === 'created' || u === 'processing' || u === 'pending') return '#F59E0B';
  return '#6B7280';
};

const AdminPayoutStatusModal: React.FC<AdminPayoutStatusModalProps> = ({
  isOpen,
  onClose,
  statusData,
  loading = false,
}) => {
  const [showFullResponse, setShowFullResponse] = useState(false);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal" onClick={(e) => e.stopPropagation()}>
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">Live status</h2>
            <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="admin-payout-status-content" style={{ padding: '40px', textAlign: 'center' }}>
            <div
              className="loading-spinner"
              style={{
                margin: '0 auto 20px',
                width: '40px',
                height: '40px',
                border: '4px solid #f3f4f6',
                borderTopColor: '#667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <p style={{ margin: 0, color: '#6b7280' }}>Fetching live status from provider…</p>
          </div>
        </div>
      </div>
    );
  }

  if (statusData?.error) {
    const src = statusData?.source as AdminPayoutStatusSource | undefined;
    const label = src === 'poolpay' ? 'PoolPay' : src === 'viyona' ? 'Viyona' : 'Provider';
    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal" onClick={(e) => e.stopPropagation()}>
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">Live status ({label})</h2>
            <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="admin-payout-status-content" style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ fontSize: '48px', margin: '0 0 16px' }}>⚠️</p>
            <p style={{ color: '#dc2626', fontWeight: 600, margin: 0 }}>{statusData.error}</p>
          </div>
          <div className="admin-payout-status-footer">
            <button type="button" className="admin-payout-status-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  const source: AdminPayoutStatusSource =
    statusData?.source ?? (statusData?.poolpay != null ? 'poolpay' : 'viyona');
  const data = statusData?.data ?? statusData?.poolpay;

  if (!data) return null;

  // ——— PoolPay: Merchant Initiated Status Enquiry API only ———
  if (source === 'poolpay') {
    const ts = data.transaction_status != null ? String(data.transaction_status) : '';
    const dotColor = poolPayTxnColor(ts);

    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal">
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">PoolPay — live status</h2>
            <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="admin-payout-status-content admin-payout-status-content--poolpay-split">
            <div className="admin-payout-status-section">
              <div className="admin-payout-status-section-header">
                <span className="status-dot" style={{ backgroundColor: dotColor }} />
                <h3>transaction_status (PoolPay enquiry API)</h3>
              </div>
              <div className="admin-payout-status-section-body">
                <div className="status-badge-large" style={{ backgroundColor: dotColor }}>
                  {ts || '—'}
                </div>
                {(data.status != null || data.message != null) && (
                  <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#6b7280' }}>
                    Enquiry wrapper: {data.status != null ? `status=${data.status}` : ''}
                    {data.message != null ? `${data.status != null ? ' · ' : ''}message=${data.message}` : ''}
                  </p>
                )}
                <div className="status-details-list">
                  {data.order_id != null && data.order_id !== '' && (
                    <div className="status-detail-item">
                      <span className="detail-label">order_id:</span>
                      <span className="detail-value" style={{ wordBreak: 'break-all' }}>
                        {String(data.order_id)}
                      </span>
                    </div>
                  )}
                  {data.transaction_id != null && data.transaction_id !== '' && (
                    <div className="status-detail-item">
                      <span className="detail-label">transaction_id:</span>
                      <span className="detail-value" style={{ wordBreak: 'break-all' }}>
                        {String(data.transaction_id)}
                      </span>
                    </div>
                  )}
                  {data.pay_id != null && data.pay_id !== '' && (
                    <div className="status-detail-item">
                      <span className="detail-label">pay_id:</span>
                      <span className="detail-value">{String(data.pay_id)}</span>
                    </div>
                  )}
                  {data.transaction_type && (
                    <div className="status-detail-item">
                      <span className="detail-label">transaction_type:</span>
                      <span className="detail-value">{data.transaction_type}</span>
                    </div>
                  )}
                  {data.purpose && (
                    <div className="status-detail-item">
                      <span className="detail-label">purpose:</span>
                      <span className="detail-value">{data.purpose}</span>
                    </div>
                  )}
                  {data.amount != null && data.amount !== '' && (
                    <div className="status-detail-item">
                      <span className="detail-label">amount:</span>
                      <span className="detail-value amount">
                        ₹
                        {new Intl.NumberFormat('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(parseFloat(String(data.amount)))}
                      </span>
                    </div>
                  )}
                  {(data.beneficiary_name || data.beneficiary_mobile_number || data.beneficiary_account_number) && (
                    <div className="status-detail-item">
                      <span className="detail-label">beneficiary:</span>
                      <span className="detail-value">
                        {[data.beneficiary_name, data.beneficiary_mobile_number, data.beneficiary_account_number, data.beneficiary_ifsc]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </div>
                  )}
                  {data.acquirer_status_message && (
                    <div className="status-detail-item">
                      <span className="detail-label">acquirer_status_message:</span>
                      <span className="detail-value">{data.acquirer_status_message}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="admin-payout-status-section admin-payout-status-section--poolpay-raw">
              <div className="admin-payout-status-section-header">
                <span className="section-icon">📋</span>
                <h3>Raw PoolPay enquiry response</h3>
              </div>
              <div className="admin-payout-status-section-body">
                <pre className="admin-payout-status-json--poolpay-raw">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </div>
            </div>
          </div>

          <div className="admin-payout-status-footer">
            <button type="button" className="admin-payout-status-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ——— Viyona ———
  const getStatusColor = (status: string) => {
    const statusUpper = status?.toUpperCase() || '';
    switch (statusUpper) {
      case 'SUCCESS':
        return '#10B981';
      case 'FAILED':
        return '#EF4444';
      case 'PENDING':
      case 'PROCESSING':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  const vStatus = data.status || 'UNKNOWN';
  const statusColor = getStatusColor(vStatus);

  return (
    <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
      <div className="admin-payout-status-modal">
        <div className="admin-payout-status-header">
          <h2 className="admin-payout-status-title">Viyona — live status</h2>
          <button type="button" className="admin-payout-status-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="admin-payout-status-content">
          <div className="admin-payout-status-section">
            <div className="admin-payout-status-section-header">
              <span className="status-dot" style={{ backgroundColor: statusColor }} />
              <h3>Live ViyonaPay status</h3>
            </div>
            <div className="admin-payout-status-section-body">
              <div className="status-badge-large" style={{ backgroundColor: statusColor }}>
                {vStatus}
              </div>
              <div className="status-details-list">
                {data.merchant_reference_number && (
                  <div className="status-detail-item">
                    <span className="detail-label">Merchant Reference:</span>
                    <span className="detail-value">{data.merchant_reference_number}</span>
                  </div>
                )}
                {data.viyona_transaction_id && (
                  <div className="status-detail-item">
                    <span className="detail-label">Transaction ID:</span>
                    <span className="detail-value">{data.viyona_transaction_id}</span>
                  </div>
                )}
                {data.amount && (
                  <div className="status-detail-item">
                    <span className="detail-label">Amount:</span>
                    <span className="detail-value amount">
                      ₹
                      {new Intl.NumberFormat('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(parseFloat(data.amount))}
                    </span>
                  </div>
                )}
                {data.bank_reference_number && (
                  <div className="status-detail-item">
                    <span className="detail-label">Bank Reference:</span>
                    <span className="detail-value">{data.bank_reference_number}</span>
                  </div>
                )}
                {data.error_message && (
                  <div className="status-detail-item">
                    <span className="detail-label">Error Message:</span>
                    <span className="detail-value error">{data.error_message}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="admin-payout-status-section">
            <div className="admin-payout-status-section-header">
              <span className="section-icon">📄</span>
              <h3>ViyonaPay response</h3>
            </div>
            <div className="admin-payout-status-section-body">
              {data.decrypted_response ? (
                <div className="response-preview">
                  <div className="response-preview-item">
                    <span className="preview-label">Status:</span>
                    <span className="preview-value">
                      {data.decrypted_response?.response_body?.status ||
                        data.decrypted_response?.status ||
                        data.status ||
                        'N/A'}
                    </span>
                  </div>
                  <div className="response-preview-item">
                    <span className="preview-label">Transaction ID:</span>
                    <span className="preview-value">
                      {data.decrypted_response?.response_body?.transaction_id ||
                        data.decrypted_response?.transaction_id ||
                        data.viyona_transaction_id ||
                        'N/A'}
                    </span>
                  </div>
                  <div className="response-preview-item">
                    <span className="preview-label">Bank Reference:</span>
                    <span className="preview-value">
                      {data.decrypted_response?.response_body?.bank_reference_number ||
                        data.decrypted_response?.bank_reference_number ||
                        data.bank_reference_number ||
                        'N/A'}
                    </span>
                  </div>
                  {data.decrypted_response?.response_body?.error_message || data.decrypted_response?.error_message ? (
                    <div className="response-preview-item">
                      <span className="preview-label">Message:</span>
                      <span className="preview-value">
                        {data.decrypted_response?.response_body?.error_message || data.decrypted_response?.error_message}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="no-data-message">No ViyonaPay data available</div>
              )}
            </div>
          </div>
        </div>

        {data.decrypted_response && (
          <div className="admin-payout-status-full-response">
            <button
              type="button"
              className="admin-payout-status-toggle-response"
              onClick={() => setShowFullResponse(!showFullResponse)}
            >
              <span className="section-icon">📋</span>
              <h3>Complete live ViyonaPay response</h3>
              <span className="toggle-icon">{showFullResponse ? '▼' : '▶'}</span>
            </button>
            {showFullResponse && (
              <div className="admin-payout-status-json-container">
                <pre className="admin-payout-status-json">{JSON.stringify(data.decrypted_response, null, 2)}</pre>
              </div>
            )}
          </div>
        )}

        <div className="admin-payout-status-footer">
          <button type="button" className="admin-payout-status-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPayoutStatusModal;
